import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Clock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const CATEGORY_LABELS = {
  gbp_readiness: 'GBP',
  website_relevance: 'Website',
  technical_seo: 'Technical',
  prominence: 'Prominence',
  risk_compliance: 'Risk'
};
const CATEGORY_COLORS = {
  gbp_readiness: '#0268C4',
  website_relevance: '#22C55E',
  technical_seo: '#DDA73D',
  prominence: '#64748B',
  risk_compliance: '#EF4444'
};

export default function HistoryPage() {
  const { projectId } = useParams();
  const [audits, setAudits] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/audits`)
    ]).then(([pRes, aRes]) => {
      setProject(pRes.data);
      setAudits(aRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  // Chart data: reverse so oldest is first
  const chartData = [...audits].reverse().filter(a => a.status === 'completed').map((a, i) => ({
    name: new Date(a.completed_at || a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: a.overall_score || 0,
    ...(a.category_scores || {})
  }));

  const getScoreTrend = () => {
    if (chartData.length < 2) return null;
    const last = chartData[chartData.length - 1].score;
    const prev = chartData[chartData.length - 2].score;
    const diff = last - prev;
    if (diff > 0) return { icon: TrendingUp, color: '#22C55E', text: `+${diff} from last audit` };
    if (diff < 0) return { icon: TrendingDown, color: '#EF4444', text: `${diff} from last audit` };
    return { icon: Minus, color: '#64748B', text: 'No change' };
  };

  const trend = getScoreTrend();

  if (loading) return <div className="min-h-screen bg-[#F7F9FC]"><Navbar /><div className="max-w-5xl mx-auto px-4 py-12 animate-pulse"><div className="h-8 bg-[#EFF3F8] rounded w-1/3" /></div></div>;

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to={`/projects/${projectId}`} data-testid="back-to-project" className="inline-flex items-center gap-1 text-sm text-[#64748B] hover:text-[#0268C4] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Project
        </Link>

        <h1 data-testid="history-heading" className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F2951] mb-1">Audit History</h1>
        <p className="text-base text-[#64748B] mb-8">{project?.business_name} — Track your SEO progress over time</p>

        {audits.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E2E8F0]">
            <p className="text-[#64748B]">No audits yet. Run your first audit to start tracking trends.</p>
          </div>
        ) : (
          <>
            {/* Health Score Chart */}
            {chartData.length > 0 && (
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)] mb-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-lg font-medium text-[#0F2951]">Health Score Over Time</h2>
                  {trend && (
                    <div className="flex items-center gap-1.5 text-sm" style={{ color: trend.color }}>
                      <trend.icon className="w-4 h-4" /> {trend.text}
                    </div>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fill: '#64748B', fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 4px 24px rgba(44,48,46,0.08)' }} />
                    <Legend />
                    <Line type="monotone" dataKey="score" stroke="#0268C4" strokeWidth={2.5} dot={{ r: 4, fill: '#0268C4' }} name="Overall Score" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Category Breakdown Chart */}
            {chartData.length > 0 && chartData[0].gbp_readiness !== undefined && (
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)] mb-6">
                <h2 className="font-heading text-lg font-medium text-[#0F2951] mb-6">Category Scores Over Time</h2>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fill: '#64748B', fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }} />
                    <Legend />
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <Line key={key} type="monotone" dataKey={key} stroke={CATEGORY_COLORS[key]} strokeWidth={2} dot={{ r: 3 }} name={label} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Audit History List */}
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
              <h2 className="font-heading text-lg font-medium text-[#0F2951] mb-4">All Audits</h2>
              <div className="space-y-3">
                {audits.map((audit, i) => {
                  const scoreColor = (audit.overall_score || 0) > 80 ? '#22C55E' : (audit.overall_score || 0) > 50 ? '#DDA73D' : '#EF4444';
                  return (
                    <div key={audit.id} data-testid={`audit-history-${i}`} className="flex items-center gap-4 p-4 bg-[#F7F9FC] rounded-xl">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-heading font-semibold" style={{ backgroundColor: audit.status === 'completed' ? scoreColor : '#64748B' }}>
                        {audit.status === 'completed' ? audit.overall_score : '—'}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#0F2951]">
                          Audit #{audits.length - i}
                          {audit.status !== 'completed' && <Badge variant="outline" className="ml-2 text-xs">{audit.status}</Badge>}
                        </p>
                        <p className="text-xs text-[#64748B] flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" />
                          {new Date(audit.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="text-xs text-[#64748B]">{audit.issue_count || 0} issues</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
